import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarClock } from "lucide-react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

const SECTION_LABELS = {
  marquee: "Marquee banner",
  philosophy: "Engineering Philosophy",
  services: "Services",
  products: "Featured Products",
  why: "Why BuildRaq",
  process: "Development Process",
  tech: "Technology Stack",
  "case-studies": "Case Studies",
  testimonials: "Testimonials",
  founder: "Founder Story",
  faq: "FAQ",
};

export default function SettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scheduler, setScheduler] = useState("");
  const [sections, setSections] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings");
        setScheduler(data.scheduler_url || "");
        setSections(data.sections || {});
      } catch { toast.error("Failed to load settings"); }
      finally { setLoading(false); }
    })();
  }, []);

  const saveScheduler = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { scheduler_url: scheduler });
      toast.success("Scheduler link saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const toggleSection = async (key) => {
    const next = { ...sections, [key]: !sections[key] };
    setSections(next);
    try {
      await api.put("/admin/settings", { sections: { [key]: next[key] } });
    } catch { toast.error("Save failed"); setSections(sections); }
  };

  if (loading) return <div className="flex items-center gap-3 text-muted-foreground py-16 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;

  return (
    <div data-testid="panel-settings" className="max-w-2xl space-y-10">
      <div>
        <h2 className="font-heading text-2xl font-medium text-white">Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">Booking link and section visibility.</p>
      </div>

      <div className="rounded-2xl border border-white/5 bg-white/[0.015] p-6">
        <div className="flex items-center gap-2 mb-4">
          <CalendarClock className="h-5 w-5 text-brand" />
          <h3 className="font-heading text-lg text-white">Discovery-call scheduler</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Paste your Calendly (or similar) link. Once set, every “Book a Discovery Call” button opens it in a new tab. Leave empty to scroll visitors to the contact form.
        </p>
        <Label className="text-sm text-white/80">Scheduler URL</Label>
        <div className="mt-2 flex gap-3">
          <Input
            value={scheduler}
            onChange={(e) => setScheduler(e.target.value)}
            placeholder="https://calendly.com/your-team/discovery"
            data-testid="scheduler-input"
            className="bg-black/30 border-white/10 text-white placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-brand rounded-lg"
          />
          <button onClick={saveScheduler} disabled={saving} data-testid="save-scheduler-btn" className="rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-5 inline-flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/5 bg-white/[0.015] p-6">
        <h3 className="font-heading text-lg text-white mb-1">Section visibility</h3>
        <p className="text-sm text-muted-foreground mb-5">Toggle sections off to hide them from the live homepage.</p>
        <div className="divide-y divide-white/5">
          {Object.keys(SECTION_LABELS).map((key) => (
            <div key={key} className="flex items-center justify-between py-3.5">
              <span className="text-sm text-white/90">{SECTION_LABELS[key]}</span>
              <Switch
                checked={sections[key] !== false}
                onCheckedChange={() => toggleSection(key)}
                data-testid={`section-toggle-${key}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
